import { AlertTriangle } from 'lucide-react'
import { WorkflowErrorCodes } from '@/lib/workflowErrorCodes'
import { TokenBudgetIcon } from '@/components/projects/chat/TokenBudgetIcon'
import { formatUsd } from '@/lib/currencyFormatters'

interface HaltedRunBannerProps {
  errorCode: string
  phaseName: string
  stepName: string
  errorMessage?: string | null
  downstreamSteps?: string[]
  currentSpendUsd?: number | null
  capUsd?: number | null
}

export function HaltedRunBanner({ errorCode, phaseName, stepName, errorMessage, downstreamSteps, currentSpendUsd, capUsd }: Readonly<HaltedRunBannerProps>) {
  const isTokenExhausted = errorCode === WorkflowErrorCodes.TokenExhausted
  const isConnectorExpired = errorCode === WorkflowErrorCodes.ConnectorExpired
  const isTitanUnavailable = errorCode === WorkflowErrorCodes.TitanUnavailable

  const title = isTokenExhausted
    ? 'Halted — Token exhausted'
    : isConnectorExpired
      ? 'Halted — Connector token expired'
      : isTitanUnavailable
        ? 'Halted — Qubiz Titan unavailable'
        : 'Halted — Run stopped with an error'

  const badgeLabel = typeof currentSpendUsd === 'number' && typeof capUsd === 'number'
    ? `${formatUsd(currentSpendUsd)} / ${formatUsd(capUsd)}`
    : null

  const hasBadge = isTokenExhausted && badgeLabel !== null

  const downstreamLabel = downstreamSteps && downstreamSteps.length > 0
    ? downstreamSteps.join(', ')
    : null

  return (
    <div className="mt-3" role="alert" data-testid="halted-run-banner">
      <div className="rounded-md border border-danger-foreground bg-danger px-4 py-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-danger-foreground mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2">
              <p className="text-sm font-semibold text-danger-foreground">{title}</p>
              {hasBadge && (
                <span className="inline-flex items-center gap-1 rounded-full border border-danger-foreground px-2 py-0.5 text-xs font-medium text-danger-foreground shrink-0">
                  <TokenBudgetIcon className="h-3 w-3" />
                  {badgeLabel}
                </span>
              )}
            </div>
            {isTokenExhausted ? (
              <p className="mt-1 text-sm text-danger-foreground">
                Run halted on <span className="font-medium">{phaseName}</span> {'→'} <span className="font-medium">{stepName}</span>. The step exceeded its configured token budget before completing.{' '}
                {downstreamLabel
                  ? <>Downstream steps (<span className="font-medium">{downstreamLabel}</span>) and the approval gate are blocked.</>
                  : 'Downstream steps and the approval gate are blocked.'
                }{' '}
                Increase the budget for this step, or acknowledge the failure and override to continue.
              </p>
            ) : isConnectorExpired ? (
              <p className="mt-1 text-sm text-danger-foreground">
                Run halted on <span className="font-medium">{phaseName}</span> {'→'} <span className="font-medium">{stepName}</span>. A connected resource token expired during execution. Reconnect the connector and retry the step to continue.
              </p>
            ) : isTitanUnavailable ? (
              <p className="mt-1 text-sm text-danger-foreground">
                Run halted on <span className="font-medium">{phaseName}</span> {'→'} <span className="font-medium">{stepName}</span>.{' '}
                {errorMessage ?? "Qubiz Titan could not be used. Check the endpoint in the tenant settings and your Qubiz Titan key on the project's Connectors page, then retry the step."}
              </p>
            ) : (
              <p className="mt-1 text-sm text-danger-foreground">
                Run halted on <span className="font-medium">{phaseName}</span> {'→'} <span className="font-medium">{stepName}</span>. The run stopped due to an unexpected error. Review the step log for details.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
